<template>
  <USlider
    :model-value="modelValue"
    :min="min"
    :max="max"
    :step="step"
    :name="name"
    :disabled="disabled"
    v-bind="controlAttributes"
    @update:model-value="handleUpdate"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import USlider from '@nuxt/ui/components/Slider.vue'

const props = withDefaults(
  defineProps<{
    /** Controlled numeric value. */
    modelValue: number
    /** Lower bound of the control. */
    min: number
    /** Upper bound of the control. */
    max: number
    /** Increment between selectable values. */
    step: number
    /** HTML form field name. */
    name: string
    /** Whether user interaction with the control is disabled. */
    disabled?: boolean
    /** Id of the element describing this control, for assistive tech. */
    ariaDescribedby?: string | undefined
    /** Accessible name of the slider thumb. */
    ariaLabel?: string | undefined
    /** Spoken value of the slider thumb, e.g. a formatted amount. */
    ariaValuetext?: string | undefined
    /** Values a pointer drag lands on when it passes within `snapRadius` of one. */
    snapPoints?: readonly number[]
    /** Distance from a snap point within which a pointer drag lands on it. */
    snapRadius?: number
    /** Whether the control currently carries a validation failure. */
    invalid?: boolean
  }>(),
  {
    ariaDescribedby: undefined,
    ariaLabel: undefined,
    ariaValuetext: undefined,
    disabled: false,
    invalid: false,
    snapPoints: () => [],
    snapRadius: 0
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: number]
  blurred: []
}>()

// USlider's generated props lack an index signature; one v-bind spread forwards aria attrs and the focusout listener (blur does not bubble).
const controlAttributes = computed(() => ({
  'aria-describedby': props.ariaDescribedby,
  'aria-invalid': props.invalid ? 'true' : undefined,
  'aria-label': props.ariaLabel,
  'aria-valuetext': props.ariaValuetext,
  onFocusout: handleBlur,
  onLostpointercapture: endPointerDrag,
  onPointercancel: endPointerDrag,
  onPointerdownCapture: startPointerDrag,
  onPointerup: endPointerDrag
}))

// Keyboard steps never snap, so a key press can always leave a snap point.
let isPointerDragging = false

function startPointerDrag(): void {
  isPointerDragging = true
}

function endPointerDrag(): void {
  isPointerDragging = false
}

function snapped(value: number): number {
  if (!isPointerDragging) return value
  const nearest = props.snapPoints.reduce<number | undefined>(
    (best, point) =>
      Math.abs(point - value) <= props.snapRadius &&
      (best === undefined || Math.abs(point - value) < Math.abs(best - value))
        ? point
        : best,
    undefined
  )
  return nearest ?? value
}

// Reka UI's internal v-model relay can emit the array-shaped intermediate value
// before unwrapping to a number; only the number form is a real value change.
function handleUpdate(value: unknown): void {
  if (typeof value === 'number') emit('update:modelValue', snapped(value))
}

function handleBlur(): void {
  emit('blurred')
}
</script>
